import { z } from 'zod';
import { birthDateSchema, birthTimeSchema, birthPlaceSchema, birthTimezoneSchema } from './birth-data';

// Age validation constants
const MIN_AGE_YEARS = 13;
const MAX_AGE_YEARS = 130;
const LAST_MONTH_INDEX = 11;
const LAST_DAY_OF_MONTH = 31;

// Schema compartilhado para dados de nascimento normalizados
export const normalizedBirthDataSchema = z
  .object({
    date: birthDateSchema.optional(),
    time: birthTimeSchema.optional(),
    timezone: birthTimezoneSchema.optional(),
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
    place: birthPlaceSchema.optional(),
  })
  .optional()
  .nullable();

// Schema compartilhado para o payload de onboarding
export const onboardingDataSchema = z.object({
  name: z.string().min(2),
  zodiacSign: z.string(), // Pode ser um enum se você tiver um compartilhado
  dob: z
    .string()
    .refine((date) => {
      const parsed = new Date(date);
      return !Number.isNaN(parsed.getTime());
    }, 'Invalid birth date format')
    .refine((date) => {
      const parsed = new Date(date);
      const today = new Date();
      return new Date(today.getFullYear() - MAX_AGE_YEARS, 0, 1) <= parsed && parsed <= today;
    }, `Age must be between ${MIN_AGE_YEARS} and ${MAX_AGE_YEARS} years`),
  birthTime: birthTimeSchema,
  birthPlace: birthPlaceSchema,
  normalizedBirthData: normalizedBirthDataSchema,
  birthPlaceTimezone: birthTimezoneSchema.optional().nullable(),
  coreConcern: z.enum(['Love', 'Money', 'Trust', 'Direction']).optional().nullable(),
  clarityWindow: z.enum(['7d', '30d', 'crisis']).optional().nullable(),
  personContext: z
    .enum(['partner', 'crush', 'ex', 'self', 'friend', 'family', 'colleague'])
    .optional()
    .nullable(),
  primaryFear: z
    .enum([
      'betrayal',
      'losing_money',
      'stuck',
      'miss_chance',
      'debt',
      'bad_luck',
      'stagnation',
      'lost',
      'overwhelmed',
      'numb',
    ])
    .optional()
    .nullable(),
});

// Schema para o formulário de onboarding do cliente (inclui interests)
export const onboardingFormSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  birthDate: z.string().min(1, 'Birth date is required'),
  birthTime: z.string().optional(),
  birthPlace: z.string().min(1, 'Birth place is required'),
  zodiacSign: z.string().min(1, 'Zodiac sign is required'),
  interests: z.array(z.string()).min(1, 'Select at least one interest'),
});

export const onboardingStepSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  birthDate: z
    .string()
    .min(1, 'Birth date is required')
    .refine((date) => {
      const parsedDate = new Date(date);
      return !Number.isNaN(parsedDate.getTime());
    }, 'Invalid birth date')
    .refine((date) => {
      const parsedDate = new Date(date);
      const today = new Date();
      parsedDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      return parsedDate <= today;
    }, 'Birth date cannot be in the future')
    .refine((date) => {
      const parsedDate = new Date(date);
      const today = new Date();
      const minDate = new Date(today.getFullYear() - MAX_AGE_YEARS, 0, 1);
      return parsedDate >= minDate;
    }, `You must be younger than ${MAX_AGE_YEARS} years old`)
    .refine((date) => {
      const parsedDate = new Date(date);
      const today = new Date();
      const maxDate = new Date(
        today.getFullYear() - MIN_AGE_YEARS,
        LAST_MONTH_INDEX,
        LAST_DAY_OF_MONTH
      );
      return parsedDate <= maxDate;
    }, `You must be at least ${MIN_AGE_YEARS} years old`),
  birthTime: z.string().optional(),
  birthPlace: z.string().min(1, 'Birth place is required'),
  interests: z.array(z.string()).min(1, 'Select at least one interest'),
});

// Type exports
export type OnboardingData = z.infer<typeof onboardingDataSchema>;
export type OnboardingFormData = z.infer<typeof onboardingFormSchema>;
export type NormalizedBirthData = z.infer<typeof normalizedBirthDataSchema>;
export type OnboardingStepData = z.infer<typeof onboardingStepSchema>;
